import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Grid,
  IconButton,
  Divider,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../api/client';
import API_CONFIG from '../config/api';

const novoParticipante = () => ({ nome: '', email: '', cpf: '' });

function formatCpfInput(value) {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

const camposObrigatoriosPreenchidos = (titulo, arquivo, participantes) => {
  if (!titulo || !arquivo || participantes.length === 0) return false;
  return participantes.every((participante) => participante.nome.trim() && participante.email.trim());
};

async function baixarArquivoComoFile(url, nomeFallback = 'documento.pdf') {
  if (!url) return null;
  let pathOrUrl = url;
  if (url.startsWith(API_CONFIG.BACKEND_URL)) {
    pathOrUrl = url.slice(API_CONFIG.BACKEND_URL.length) || '/';
  }
  const res = await apiFetch(pathOrUrl);
  if (!res.ok) throw new Error('Não foi possível carregar o PDF de origem.');
  const blob = await res.blob();
  const nome = nomeFallback.replace(/[<>:"/\\|?*]/g, '_');
  return new File([blob], nome.endsWith('.pdf') ? nome : `${nome}.pdf`, { type: 'application/pdf' });
}

export default function RegulamentoAssinaturasNovoDocumentoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, unidadeSelecionada } = useAuth();
  const prefill = location.state || {};

  const [titulo, setTitulo] = useState(prefill.titulo || '');
  const [descricao, setDescricao] = useState(prefill.descricao || '');
  const [arquivo, setArquivo] = useState(null);
  const [arquivoNome, setArquivoNome] = useState('');
  const [participantes, setParticipantes] = useState(
    Array.isArray(prefill.participantes) && prefill.participantes.length
      ? prefill.participantes.map((p) => ({
          nome: p.nome || '',
          email: p.email || '',
          cpf: p.cpf || '',
        }))
      : [novoParticipante()]
  );
  const [salvando, setSalvando] = useState(false);
  const [carregandoPrefill, setCarregandoPrefill] = useState(Boolean(prefill.arquivoUrl));
  const [usuariosEquipe, setUsuariosEquipe] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const vinculo = useMemo(
    () => ({
      processoId: prefill.processoId || null,
      atividadeId: prefill.atividadeId || null,
      solicitacaoId: prefill.solicitacaoId || null,
      processoCodigo: prefill.processoCodigo || null,
      fromContratos: Boolean(prefill.fromContratos),
    }),
    [prefill]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = unidadeSelecionada?.codigo
          ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
          : '';
        const res = await apiFetch(`/api/usuarios${qs}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const ativos = (Array.isArray(data) ? data : []).filter((u) => !u.inativo);
        if (!cancelled) setUsuariosEquipe(ativos);
      } catch {
        if (!cancelled) setUsuariosEquipe([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unidadeSelecionada?.codigo]);

  useEffect(() => {
    let cancelled = false;
    async function loadPrefillPdf() {
      if (!prefill.arquivoUrl) {
        setCarregandoPrefill(false);
        return;
      }
      try {
        setCarregandoPrefill(true);
        const file = await baixarArquivoComoFile(
          prefill.arquivoUrl,
          `${prefill.processoCodigo || 'documento'}.pdf`
        );
        if (!cancelled && file) {
          setArquivo(file);
          setArquivoNome(file.name);
          setSnackbar({
            open: true,
            message: 'PDF do processo carregado. Revise participantes e confirme.',
            severity: 'info',
          });
        }
      } catch (e) {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: e.message || 'Não foi possível pré-carregar o PDF. Selecione manualmente.',
            severity: 'warning',
          });
        }
      } finally {
        if (!cancelled) setCarregandoPrefill(false);
      }
    }
    loadPrefillPdf();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill.arquivoUrl]);

  const podeSalvar = useMemo(
    () => camposObrigatoriosPreenchidos(titulo, arquivo, participantes),
    [titulo, arquivo, participantes]
  );

  const handleArquivoChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setSnackbar({ open: true, message: 'Envie apenas arquivos em PDF.', severity: 'warning' });
      return;
    }
    setArquivo(file);
    setArquivoNome(file.name);
  };

  const atualizarParticipante = (index, campo, valor) => {
    setParticipantes((prev) => {
      const copia = [...prev];
      copia[index] = { ...copia[index], [campo]: valor };
      return copia;
    });
  };

  const adicionarParticipante = () => {
    setParticipantes((prev) => [...prev, novoParticipante()]);
  };

  const removerParticipante = (index) => {
    setParticipantes((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async () => {
    if (!podeSalvar) {
      setSnackbar({ open: true, message: 'Preencha todas as informações obrigatórias.', severity: 'warning' });
      return;
    }

    try {
      setSalvando(true);
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      formData.append(
        'dados',
        JSON.stringify({
          titulo,
          descricao,
          processoId: vinculo.processoId,
          atividadeId: vinculo.atividadeId,
          solicitacaoId: vinculo.solicitacaoId,
          tipo: prefill.tipoDocumento || prefill.tipo || null,
          tipoFluxo: prefill.tipoDocumento || prefill.tipoFluxo || null,
          criadoPor: user
            ? {
                id: user.id,
                nome: user.nome,
                email: user.email,
              }
            : null,
          participantes: participantes.map((participante, index) => ({
            ordem: index + 1,
            nome: participante.nome.trim(),
            email: participante.email.trim(),
            cpf: participante.cpf.trim(),
          })),
        })
      );

      const params = new URLSearchParams();
      if (unidadeSelecionada?.codigo) params.set('unidade', unidadeSelecionada.codigo);
      const qs = params.toString();
      const response = await apiFetch(qs ? `/api/assinaturas/documentos?${qs}` : '/api/assinaturas/documentos', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Erro ao criar documento.');
      }

      setSnackbar({ open: true, message: 'Documento criado com sucesso!', severity: 'success' });
      window.dispatchEvent(
        new CustomEvent('contratos:refresh', {
          detail: {
            source: 'assinaturas',
            tipo: 'documento_criado',
            processoId: vinculo.processoId,
            assinaturaDocumentoId: data?.id,
          },
        })
      );
      navigate(`/assinaturas/documentos/${data.id}`);
    } catch (error) {
      console.error('Erro ao criar documento:', error);
      setSnackbar({ open: true, message: error.message || 'Erro ao criar documento.', severity: 'error' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
            Novo Documento para Assinatura
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Envie o documento em PDF, defina os participantes e a ordem de assinatura.
          </Typography>
          {vinculo.fromContratos && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip size="small" color="primary" label="Origem: Contratos / Atividades" />
              {vinculo.processoCodigo && (
                <Chip size="small" variant="outlined" label={`Processo ${vinculo.processoCodigo}`} />
              )}
              {vinculo.processoId && (
                <Chip size="small" variant="outlined" label={`ID processo ${vinculo.processoId}`} />
              )}
            </Stack>
          )}
        </Box>

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(25,118,210,0.12)' }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Informações gerais
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Título do documento"
                    value={titulo}
                    onChange={(event) => setTitulo(event.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={carregandoPrefill ? <CircularProgress size={16} /> : <UploadFileOutlinedIcon />}
                    disabled={carregandoPrefill}
                    sx={{ height: '100%' }}
                  >
                    {carregandoPrefill ? 'Carregando PDF…' : 'Selecionar PDF'}
                    <input type="file" accept="application/pdf" hidden onChange={handleArquivoChange} />
                  </Button>
                  {arquivoNome && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {arquivoNome}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descrição (opcional)"
                    value={descricao}
                    onChange={(event) => setDescricao(event.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Participantes
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={adicionarParticipante}
                  sx={{ textTransform: 'none' }}
                >
                  Adicionar participante
                </Button>
              </Stack>

              <Stack spacing={2} sx={{ mt: 2 }}>
                {participantes.map((participante, index) => (
                  <Paper
                    key={`participante-${index}`}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: 'rgba(25,118,210,0.16)',
                      background: 'rgba(248,250,255,0.6)',
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          freeSolo
                          options={usuariosEquipe}
                          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt?.nome || '')}
                          filterOptions={(options, { inputValue }) => {
                            const q = String(inputValue || '')
                              .normalize('NFD')
                              .replace(/[\u0300-\u036f]/g, '')
                              .toLowerCase()
                              .trim();
                            if (!q) return options.slice(0, 40);
                            return options
                              .filter((u) => {
                                const nome = String(u.nome || '')
                                  .normalize('NFD')
                                  .replace(/[\u0300-\u036f]/g, '')
                                  .toLowerCase();
                                const email = String(u.email || '').toLowerCase();
                                return nome.includes(q) || email.includes(q);
                              })
                              .slice(0, 40);
                          }}
                          value={
                            usuariosEquipe.find(
                              (u) =>
                                String(u.nome || '').trim().toLowerCase() ===
                                String(participante.nome || '').trim().toLowerCase()
                            ) || participante.nome || null
                          }
                          onChange={(_, selected) => {
                            if (selected && typeof selected === 'object') {
                              setParticipantes((prev) =>
                                prev.map((p, i) =>
                                  i === index
                                    ? {
                                        ...p,
                                        nome: selected.nome || '',
                                        email: selected.email || p.email,
                                        cpf: selected.cpf
                                          ? formatCpfInput(selected.cpf)
                                          : p.cpf,
                                      }
                                    : p
                                )
                              );
                              return;
                            }
                            atualizarParticipante(index, 'nome', typeof selected === 'string' ? selected : '');
                          }}
                          onInputChange={(_, value, reason) => {
                            if (reason === 'input') atualizarParticipante(index, 'nome', value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Nome completo"
                              required
                              helperText="Busque pelo nome da Gestão de Equipe para preencher e-mail e CPF"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="E-mail"
                          value={participante.email}
                          onChange={(event) => atualizarParticipante(index, 'email', event.target.value)}
                          fullWidth
                          required
                          type="email"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="CPF"
                          value={participante.cpf}
                          onChange={(event) =>
                            atualizarParticipante(index, 'cpf', formatCpfInput(event.target.value))
                          }
                          fullWidth
                          placeholder="000.000.000-00"
                        />
                      </Grid>
                      <Grid item xs={12} md={1} display="flex" justifyContent="flex-end">
                        {participantes.length > 1 && (
                          <IconButton color="error" onClick={() => removerParticipante(index)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`Ordem ${index + 1}`} size="small" color="primary" variant="outlined" />
                      <Chip label="Assinatura eletrônica" size="small" color="success" variant="outlined" />
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="text"
                onClick={() => (vinculo.fromContratos ? navigate('/contratos/atividades') : navigate(-1))}
                disabled={salvando}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                disabled={!podeSalvar || salvando || carregandoPrefill}
                onClick={handleSubmit}
                sx={{ textTransform: 'none', px: 4 }}
              >
                {salvando ? 'Salvando...' : 'Criar documento'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
